/**
 * Checking that a set of preset coefficients is being applied to the imagery it was
 * fitted for.
 * Preset mode skips the fitting and applies numbers the user supplies - from an earlier run, from a
 * paper, from a colleague. Those numbers only mean anything for the sensor and the bands they came
 * out of: Lyzenga coefficients fitted on Sentinel's green and blue describe a relationship between
 * *those* reflectances and depth. Applied to Landsat, the arithmetic still works and a depth map
 * still comes out - plausible-looking, and wrong, with nothing on screen to say so.
 * So the sensor the preset belongs to travels with it, and is compared against the one in use
 * before the model is applied.
 */

/**
 * @function presetContextOf
 * @description The imagery a set of coefficients belongs to: which satellite, at which processing
 * level, through which bands.
 * @param {Object} satellite - The satellite, as `getSelectedSatellite` returns it.
 * @param {String} type - Processing level, `'TOA'` or `'SR'`.
 * @param {Array<String>} bands - The bands the coefficients were fitted through; order does not
 * matter, see {@link presetMismatch}.
 * **Called by:
 * @see {AOIChooser} - when the user enters preset coefficients.
 * @memberof module:Bathymetry
 * @returns {Object} `{satellite, type, bands}`.
 */
export const presetContextOf = (satellite, type, bands) => ({
  satellite: satellite?.name || '',
  type: type || '',
  bands: Array.isArray(bands) ? bands.filter(Boolean) : [],
});

/**
 * @function describePresetContext
 * @description One readable line for a context, to tell the user which imagery is which.
 * @param {Object} context - As {@link presetContextOf} builds it.
 * @memberof module:Bathymetry
 * @returns {String} e.g. `Landsat TOA (B2, B3, B4)`.
 */
export const describePresetContext = (context) => {
  if (!context) return '';
  const bands = context.bands?.length ? ` (${context.bands.join(', ')})` : '';
  return `${[context.satellite, context.type].filter(Boolean).join(' ')}${bands}`.trim();
};

/**
 * @function presetMismatch
 * @description Whether preset coefficients are about to be applied to imagery other than the one
 * they were entered for.
 * Says nothing when there is no preset, and nothing when the preset carries no context - sessions
 * saved before the context was recorded are let through rather than blocked, since the user was
 * never asked for it.
 * Bands are compared as sets: the same three bands listed in another order are the same three bands,
 * and the model is handed them by name.
 * @param {Object} preset - The context stored with the coefficients.
 * @param {Object} current - The context of the imagery now selected.
 * **Called by:
 * @see {PeriodChooser} - before going on to the result.
 * @see {ProcessingPage} - before applying the model, as the last line of defence.
 * @memberof module:Bathymetry
 * @returns {Object|null} `{expected, actual}` as readable lines, or null when they agree.
 */
export const presetMismatch = (preset, current) => {
  if (!preset?.satellite || !current?.satellite) return null;

  const sameSatellite = preset.satellite === current.satellite;
  const sameType = !preset.type || !current.type || preset.type === current.type;
  const presetBands = [...(preset.bands || [])].sort();
  const currentBands = [...(current.bands || [])].sort();
  const sameBands =
    !presetBands.length ||
    !currentBands.length ||
    (presetBands.length === currentBands.length && presetBands.every((band, i) => band === currentBands[i]));

  if (sameSatellite && sameType && sameBands) return null;

  return {
    expected: describePresetContext(preset),
    actual: describePresetContext(current),
  };
};
